import BaseModal, {type ModalTransitionProps} from "@restart/ui/Modal";
import {CSSTransition} from "react-transition-group";
import * as React from "react";
import {createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef} from "react";
import {type ModalSize} from "@/shared/ui/bootstrapModal";
import type {ModalProps as RBModalProps, ModalHeaderProps as RBModalHeaderProps, ModalTitleProps as RBModalTitleProps, ModalFooterProps as RBModalFooterProps} from "react-bootstrap";
import type {ModalBodyProps as RBModalBodyProps} from "react-bootstrap/esm/ModalBody";

export type {ModalSize};

/**
 * Drops the `[other: string]: any` index signature react-bootstrap declares on its prop types.
 * Without this, `Omit`/`keyof` collapse every inherited prop to `any` (the literal keys are
 * absorbed into the index signature's `string`), so the homomorphic remap recovers the real types.
 */
type RemoveIndexSignature<T> = {
	[K in keyof T as string extends K ? never : number extends K ? never : K]: T[K];
};

/**
 * React-Bootstrap v5 API targeting Bootstrap 3 CSS.
 * See https://react-bootstrap.netlify.app/docs/components/modal#api
 * Intentional deviations:
 *   - "bsPrefix" omitted — Bootstrap 5 CSS theming, not applicable
 *   - "fullscreen" omitted — requires Bootstrap 5 CSS, not implemented
 *   - "dialogAs" omitted — custom dialog wrapper not supported
 *   - "size" replaced with Bootstrap 3 CSS class name values (ModalSize)
 *   - "transparent" added — applies .modal-transparent to .modal-content
 *   - "onEscapeKeyDown" / "onBackdropClick" added — escape hatches for close-source tracking
 */
export interface ModalProps extends Omit<RemoveIndexSignature<RBModalProps>, "bsPrefix" | "fullscreen" | "dialogAs" | "size" | "children"> {
	size?: ModalSize;
	transparent?: boolean;
	/** Forwarded to the dialog element to label the modal for assistive technology. */
	"aria-labelledby"?: string;
	/** Escape hatch: called before onHide when the escape key is pressed. */
	onEscapeKeyDown?: (event: KeyboardEvent) => void;
	/** Escape hatch: called before onHide when the backdrop is clicked (only when backdrop={true}). */
	onBackdropClick?: (event: React.SyntheticEvent) => void;
	children: ReactNode;
}

/**
 * Deviations from v5:
 *   - "bsPrefix" omitted
 *   - "closeVariant" omitted — Bootstrap 5 only
 *   - "onHide" omitted — supplied via context, not as a prop
 */
export type ModalHeaderProps = Omit<RBModalHeaderProps, "bsPrefix" | "closeVariant" | "onHide">;

/** "bsPrefix" omitted. "as" prop still available for polymorphic rendering. */
export type ModalTitleProps = Omit<RBModalTitleProps, "bsPrefix">;

/**
 * "bsPrefix" omitted.
 * "scrollable" added — Bootstrap 3 puts scrollable behaviour on Body, not the root Modal.
 */
export type ModalBodyProps = Omit<RBModalBodyProps, "bsPrefix"> & {scrollable?: boolean};

/** "bsPrefix" omitted. */
export type ModalFooterProps = Omit<RBModalFooterProps, "bsPrefix">;

interface ModalContextValue {
	onHide: () => void;
}

const ModalContext = createContext<ModalContextValue | null>(null);

function classNames(...classes: Array<string | false | null | undefined>) {
	return classes.filter(Boolean).join(" ");
}

function useModalContext(componentName: string): ModalContextValue {
	const ctx = useContext(ModalContext);
	if (!ctx) throw new Error(`${componentName} must be used inside Modal.`);
	return ctx;
}

const MODAL_TRANSITION_MS = 300;
const BACKDROP_TRANSITION_MS = 150;

const bootstrapInClassNames = {
	appearActive: "in",
	appearDone: "in",
	enterActive: "in",
	enterDone: "in",
};

// @restart/ui TransitionCallbacks uses (node, isAppearing) signatures while
// @types/react-transition-group@4.4.12 uses (isAppearing) / () signatures.
// The cast bridges this external library mismatch; runtime behaviour is correct.
type AnyTransitionCallbacks = Record<string, ((...args: unknown[]) => unknown) | undefined>;

function ModalDialogTransition({in: inProp, appear, unmountOnExit, children, ...callbacks}: ModalTransitionProps) {
	return (
		<CSSTransition
			in={inProp}
			appear={appear}
			unmountOnExit={unmountOnExit}
			timeout={MODAL_TRANSITION_MS}
			classNames={bootstrapInClassNames}
			{...(callbacks as AnyTransitionCallbacks)}
		>
			{children}
		</CSSTransition>
	);
}

function ModalBackdropTransition({in: inProp, appear, unmountOnExit, children, ...callbacks}: ModalTransitionProps) {
	return (
		<CSSTransition
			in={inProp}
			appear={appear}
			unmountOnExit={unmountOnExit}
			timeout={BACKDROP_TRANSITION_MS}
			classNames={bootstrapInClassNames}
			{...(callbacks as AnyTransitionCallbacks)}
		>
			{children}
		</CSSTransition>
	);
}

const ModalRoot = ({
					   show,
					   onHide,
					   onShow,
					   onEnter,
					   onEntering,
					   onEntered,
					   onExit,
					   onExiting,
					   onExited,
					   animation = true,
					   size = "",
					   centered = false,
					   scrollable = false,
					   backdrop = true,
					   keyboard = true,
					   transparent = false,
					   backdropClassName,
					   container,
					   autoFocus,
					   enforceFocus,
					   restoreFocus,
					   "aria-labelledby": ariaLabelledby,
					   className,
					   dialogClassName,
					   contentClassName,
					   onEscapeKeyDown,
					   onBackdropClick,
					   children,
				   }: ModalProps) => {
	const wasVisibleRef = useRef(show);
	const onShowRef = useRef(onShow);
	useEffect(() => {
		onShowRef.current = onShow;
	}, [onShow]);

	useEffect(() => {
		const wasVisible = wasVisibleRef.current;
		if (show && !wasVisible) onShowRef.current?.();
		wasVisibleRef.current = show;
	}, [show]);

	const handleBackdropClick = useCallback((event: React.SyntheticEvent) => {
		if (backdrop === true) onBackdropClick?.(event);
	}, [backdrop, onBackdropClick]);

	const baseModalClass = classNames("modal", animation && "fade", className);
	const baseDialogClass = classNames(
		"modal-dialog",
		size,
		centered && "modal-vertical-centered",
		scrollable && "scrollable-modal",
		dialogClassName,
	);
	const baseContentClass = classNames("modal-content", transparent && "modal-transparent", contentClassName);

	const contextValue = useMemo<ModalContextValue>(() => ({onHide: onHide ?? (() => {})}), [onHide]);

	return (
		<ModalContext.Provider value={contextValue}>
			<BaseModal
				show={show}
				backdrop={backdrop}
				keyboard={keyboard}
				className={baseModalClass}
				onHide={onHide}
				onEnter={onEnter}
				onEntering={onEntering}
				onEntered={onEntered}
				onExit={onExit}
				onExiting={onExiting}
				onExited={onExited}
				transition={animation ? ModalDialogTransition : undefined}
				backdropTransition={animation ? ModalBackdropTransition : undefined}
				onEscapeKeyDown={onEscapeKeyDown}
				onBackdropClick={onBackdropClick ? handleBackdropClick : undefined}
				container={container}
				autoFocus={autoFocus}
				enforceFocus={enforceFocus}
				restoreFocus={restoreFocus}
				renderBackdrop={(props) => (
					<div {...props} className={classNames("modal-backdrop", animation && "fade", backdropClassName)} />
				)}
				renderDialog={(props) => (
					<div
						{...props}
						className={classNames(props.className)}
						aria-labelledby={ariaLabelledby}
						ref={props.ref}
						style={{...props.style, display: "block"}}
					>
						<div className={baseDialogClass}>
							<div className={baseContentClass}>{children}</div>
						</div>
					</div>
				)}
			/>
		</ModalContext.Provider>
	);
};

const ModalHeader = ({closeButton = false, closeLabel = messages["dialog.close"], className, children}: ModalHeaderProps) => {
	const {onHide} = useModalContext("Modal.Header");
	return (
		<div className={classNames("modal-header", className)}>
			{closeButton && (
				<button
					type="button"
					className="close"
					title={closeLabel}
					aria-label={closeLabel}
					onClick={onHide}
				>
					<i className="fal fa-times" />
				</button>
			)}
			{children}
		</div>
	);
};

const ModalTitle = ({as: Component = "h4", className, children}: ModalTitleProps) => (
	<Component className={classNames("modal-title", className)}>{children}</Component>
);

const ModalBody = ({as: Component = "div", scrollable = false, className, children}: ModalBodyProps) => (
	<Component className={classNames("modal-body", scrollable && "scrollable-modal", className)}>
		{children}
	</Component>
);

const ModalFooter = ({as: Component = "div", className, children}: ModalFooterProps) => (
	<Component className={classNames("modal-footer", className)}>{children}</Component>
);

export const Modal = Object.assign(ModalRoot, {
	Header: ModalHeader,
	Title: ModalTitle,
	Body: ModalBody,
	Footer: ModalFooter,
});
